import classnames from 'classnames';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import Shimmer from '../Shimmer';
import { useCallback, useState } from 'react';
import Button from '../Buttons/Button';
import {
  CreditTrackerAdminRow,
  UserTrackerDataRow
} from '../CreditTrackerAdmin/hooks/useCreditTrackerAdminHook';
import Icon from '../Icon';
import ComplexTooltip from '../Tooltip/ComplexTooltip';
import Cookies from 'js-cookie';

export interface RowInfoAction {
  text?: string;
  href?: string;
  inactive?: boolean;
  callback?: (key: number) => void;
  component?: JSX.Element;
  icon?: string;
}

export type ItemInfo = string | JSX.Element;

type TableProps = {
  isLoading?: boolean;
  isTrackerDataLoading?: boolean;
  tableHeader: string[];
  rows: CreditTrackerAdminRow[];
  myCreditTrackerPageUrl?: string;
  emailComplianceTooltipHeader?: string;
  emailComplianceTooltipBody?: string;
  creditTrackerIncompleteTooltipHeader?: string;
  creditTrackerIncompleteTooltipBody?: string;
};

const Table: React.FC<TableProps> = ({
  isLoading,
  isTrackerDataLoading,
  tableHeader,
  rows,
  myCreditTrackerPageUrl,
  emailComplianceTooltipHeader,
  emailComplianceTooltipBody,
  creditTrackerIncompleteTooltipHeader,
  creditTrackerIncompleteTooltipBody
}) => {
  const largeWindow = useMediaQuery(ScreenSizeQueries.lg);
  const tdLargeClasses = classnames('text-2 lg:py-6 lg:pr-6');
  const tBodyClasses = classnames('divide-y divide-solid divide-gray-light align-top');

  const COMPLIANCE_PERIOD_TOOLTIP_KEY = 'compliancePeriodTooltip';
  const SETUP_INCOMPLETE_TOOLTIP_KEY = 'creditSetupIncompleteTooltip';
  const [compliancePeriodTooltipDismissed, setCompliancePeriodTooltipDismissed] = useState<boolean>(
    Cookies.get(COMPLIANCE_PERIOD_TOOLTIP_KEY) != undefined
  );
  const [setupIncompleteTooltipDismissed, setSetupIncompleteTooltipDismissed] = useState<boolean>(
    Cookies.get(SETUP_INCOMPLETE_TOOLTIP_KEY) != undefined
  );
  let firstCompliancePeriodTooltipRender = true;
  let firstSetupIncompleteTooltipRender = true;

  const hasSetupIncompleteRows = rows.some(row =>
    row.userTrackerData?.some((userData: UserTrackerDataRow) => !userData.isDeadlineSpecified)
  );

  const getTooltipVisibility = (x: UserTrackerDataRow) => {
    const showSetupIncompleteTooltip =
      !x.isDeadlineSpecified &&
      firstSetupIncompleteTooltipRender &&
      !setupIncompleteTooltipDismissed;

    if (showSetupIncompleteTooltip) {
      firstSetupIncompleteTooltipRender = false;
    }

    const showCompliancePeriodTooltip =
      x.isDeadlineSpecified &&
      firstCompliancePeriodTooltipRender &&
      !compliancePeriodTooltipDismissed &&
      (setupIncompleteTooltipDismissed || !hasSetupIncompleteRows);

    if (showCompliancePeriodTooltip) {
      firstCompliancePeriodTooltipRender = false;
    }

    return { showSetupIncompleteTooltip, showCompliancePeriodTooltip };
  };

  const handleOpenCreditTracker = useCallback(
    (
      individualSk?: number,
      organizationSk?: number,
      creditRegionShortDescription?: string | null,
      openReminder: boolean = false,
      showCompliancePeriodTooltip: boolean = false,
      showSetupIncompleteTooltip: boolean = false
    ) => {
      if (
        (showCompliancePeriodTooltip && !compliancePeriodTooltipDismissed) ||
        (showSetupIncompleteTooltip && !setupIncompleteTooltipDismissed)
      )
        return;

      if (individualSk && organizationSk) {
        let url = `${myCreditTrackerPageUrl}?orgSk=${organizationSk}&individualSk=${individualSk}`;
        if (creditRegionShortDescription) {
          url += `&region=${creditRegionShortDescription}&openReminder=${openReminder}`;
        }
        window.open(url, '_blank');
      }
    },
    [myCreditTrackerPageUrl, compliancePeriodTooltipDismissed, setupIncompleteTooltipDismissed]
  );

  const renderButton = useCallback(
    (organizationSk?: number, individualSk?: number) => {
      return (
        <Button
          color="outline-black"
          label="View"
          iconRight="link-out"
          onClick={() => handleOpenCreditTracker(individualSk, organizationSk)}
        />
      );
    },
    [handleOpenCreditTracker]
  );

  const handleCloseCompliancePeriodTooltip = useCallback(() => {
    setCompliancePeriodTooltipDismissed(true);
    Cookies.set(COMPLIANCE_PERIOD_TOOLTIP_KEY, 'true', { expires: 365 });
  }, []);

  const handleCloseSetupIncompleteTooltip = useCallback(() => {
    setSetupIncompleteTooltipDismissed(true);
    Cookies.set(SETUP_INCOMPLETE_TOOLTIP_KEY, 'true', { expires: 365 });
  }, []);

  const renderIcon = useCallback(
    (
      isDeadlineSpecified?: boolean,
      showCompliancePeriodTooltip?: boolean,
      showSetupIncompleteTooltip?: boolean
    ) => {
      if (showCompliancePeriodTooltip || showSetupIncompleteTooltip) {
        const header = showCompliancePeriodTooltip
          ? emailComplianceTooltipHeader
          : creditTrackerIncompleteTooltipHeader;
        const body = showCompliancePeriodTooltip
          ? emailComplianceTooltipBody
          : creditTrackerIncompleteTooltipBody;
        const isOpen =
          isDeadlineSpecified && !compliancePeriodTooltipDismissed
            ? true
            : !isDeadlineSpecified && !setupIncompleteTooltipDismissed
              ? true
              : false;

        return (
          <div className="contents">
            <ComplexTooltip variant="dark" isOpen={isOpen}>
              <ComplexTooltip.Trigger>
                <Icon name={isDeadlineSpecified ? 'email' : 'alert'} size="medium" />
              </ComplexTooltip.Trigger>
              <ComplexTooltip.Content
                closeLabel="Close"
                onClose={() =>
                  isDeadlineSpecified
                    ? handleCloseCompliancePeriodTooltip()
                    : handleCloseSetupIncompleteTooltip()
                }
              >
                <div>
                  <p className="text-2 mb-1 font-bold">{header}</p>
                  <p className="text-2">{body}</p>
                </div>
                <ComplexTooltip.Buttons>
                  <Button
                    label={'Close'}
                    className={classnames('w-full')}
                    color="outline-white"
                    onClick={() =>
                      isDeadlineSpecified
                        ? handleCloseCompliancePeriodTooltip()
                        : handleCloseSetupIncompleteTooltip()
                    }
                  />
                </ComplexTooltip.Buttons>
              </ComplexTooltip.Content>
            </ComplexTooltip>
          </div>
        );
      }

      return <Icon name={isDeadlineSpecified ? 'email' : 'alert'} size="medium" />;
    },
    [
      handleCloseCompliancePeriodTooltip,
      handleCloseSetupIncompleteTooltip,
      compliancePeriodTooltipDismissed,
      setupIncompleteTooltipDismissed,
      emailComplianceTooltipHeader,
      emailComplianceTooltipBody,
      creditTrackerIncompleteTooltipHeader,
      creditTrackerIncompleteTooltipBody
    ]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Shimmer className="h-[52px] w-full" />
        {Array.from({ length: 6 }).map((_, index) => (
          <Shimmer className="h-[168px] w-full rounded lg:h-[100px]" key={index} />
        ))}
      </div>
    );
  }

  return (
    <div
      className="grid w-full rounded-lg border border-solid border-gray-light"
      data-component={'Table'}
    >
      <table className="text-left">
        {largeWindow ? (
          <>
            <thead className="text-2 bg-gray-feather">
              <tr>
                {tableHeader.map((item, index) => {
                  return (
                    <th className="py-4 font-medium lg:pl-8" key={`header-${index}`} scope="col">
                      {item}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className={tBodyClasses}>
              {rows.map(({ name, individualSk, organizationSk, userTrackerData }, rowIndex) => (
                <tr key={`rowLarge-${rowIndex}`}>
                  <td
                    className={classnames(
                      tdLargeClasses,
                      'text-1 font-bold text-gray-dark lg:pl-8'
                    )}
                  >
                    {name}
                  </td>
                  <td className={classnames(tdLargeClasses, 'text-1 text-gray-dark lg:pl-8')}>
                    {isTrackerDataLoading ? (
                      <Shimmer className="h-[52px] w-full" />
                    ) : userTrackerData.length > 0 ? (
                      userTrackerData.map((x, i) => (
                        <div key={i} className="mb-[20px]">
                          <div className="font-bold">{x.jurisdiction}</div>
                          {x.complianceGroup ? (
                            <div className="font-normal">{x.complianceGroup}</div>
                          ) : (
                            <div>&nbsp;</div>
                          )}
                        </div>
                      ))
                    ) : (
                      'No licenses'
                    )}
                  </td>
                  <td
                    className={classnames(
                      tdLargeClasses,
                      'text-1 font-normal text-gray-dark lg:pl-8'
                    )}
                  >
                    {isTrackerDataLoading ? (
                      <Shimmer className="h-[52px] w-full" />
                    ) : (
                      userTrackerData?.map((x, i) => {
                        const { showSetupIncompleteTooltip, showCompliancePeriodTooltip } =
                          getTooltipVisibility(x);

                        return (
                          <div
                            key={i}
                            className={classnames('mb-[20px] flex flex-col', {
                              'text-gray-medium': x.isFulfilled,
                              'text-orange hover:text-red':
                                !x.isDeadlineSpecified || x.compliancePeriodWarning,
                              'cursor-pointer': !x.isFulfilled
                            })}
                            onClick={() =>
                              !x.isFulfilled &&
                              handleOpenCreditTracker(
                                individualSk,
                                organizationSk,
                                x.creditRegionShortDescription,
                                x.isDeadlineSpecified,
                                showCompliancePeriodTooltip,
                                showSetupIncompleteTooltip
                              )
                            }
                          >
                            <div className="flex items-center gap-2">
                              {renderIcon(
                                x.isDeadlineSpecified,
                                showCompliancePeriodTooltip,
                                showSetupIncompleteTooltip
                              )}
                              {x.compliancePeriod}
                            </div>
                            {x.compliancePeriodWarning ? (
                              <div>{x.compliancePeriodWarning}</div>
                            ) : (
                              <div>&nbsp;</div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </td>
                  <td
                    className={classnames(
                      tdLargeClasses,
                      'text-1 font-normal text-gray-dark lg:pl-8'
                    )}
                  >
                    {isTrackerDataLoading ? (
                      <Shimmer className="h-[52px] w-full" />
                    ) : (
                      userTrackerData?.map((x, i) => {
                        const tdClasses = classnames(
                          'mb-[20px] text-center',
                          x.isFulfilled ? 'text-green' : ''
                        );
                        return (
                          <div key={i} className={tdClasses}>
                            <div>{x.totalForPeriod}</div>
                            <div>&nbsp;</div>
                          </div>
                        );
                      })
                    )}
                  </td>
                  <td className={classnames(tdLargeClasses, 'flex items-center justify-end')}>
                    {renderButton(organizationSk, individualSk)}
                  </td>
                </tr>
              ))}
            </tbody>
          </>
        ) : (
          // SMALL BP
          <>
            <thead className="text-2 bg-gray-feather">
              <tr>
                <th className="px-6 py-4 font-medium" scope="col">
                  {tableHeader[0]}
                </th>
              </tr>
            </thead>
            <tbody className={tBodyClasses}>
              {rows.map(({ name, userTrackerData, organizationSk, individualSk }, rowIndex) => (
                <tr key={`rowSmall-${rowIndex}`}>
                  <td className="px-6">
                    <div className="my-[20px]">{name}</div>
                    {isTrackerDataLoading ? (
                      <Shimmer className="mb-6 h-[52px] w-full" />
                    ) : userTrackerData.length > 0 ? (
                      userTrackerData.map(x => {
                        const { showSetupIncompleteTooltip, showCompliancePeriodTooltip } =
                          getTooltipVisibility(x);

                        return (
                          <div className="mb-6">
                            <div>
                              {x.jurisdiction}
                              {x.complianceGroup ? ' - ' + x.complianceGroup : ''}
                            </div>
                            <div className="flex w-full justify-between">
                              <div
                                className={classnames('flex items-center gap-1', {
                                  'text-orange': !x.isDeadlineSpecified || x.compliancePeriodWarning
                                })}
                                onClick={() =>
                                  !x.isFulfilled &&
                                  handleOpenCreditTracker(
                                    individualSk,
                                    organizationSk,
                                    x.creditRegionShortDescription,
                                    x.isDeadlineSpecified,
                                    showCompliancePeriodTooltip,
                                    showSetupIncompleteTooltip
                                  )
                                }
                              >
                                {renderIcon(
                                  x.isDeadlineSpecified,
                                  showCompliancePeriodTooltip,
                                  showSetupIncompleteTooltip
                                )}
                                {x.compliancePeriod}
                              </div>
                              <div className={classnames({ 'text-green': x.isFulfilled })}>
                                {x.totalForPeriodSmallScreen}
                              </div>
                            </div>
                            {x.compliancePeriodWarning && (
                              <div
                                onClick={() =>
                                  !x.isFulfilled &&
                                  handleOpenCreditTracker(
                                    individualSk,
                                    organizationSk,
                                    x.creditRegionShortDescription,
                                    x.isDeadlineSpecified
                                  )
                                }
                                className="ml-6 text-orange"
                              >
                                {x.compliancePeriodWarning}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      'No licenses'
                    )}
                    <div className="pb-6">{renderButton(organizationSk, individualSk)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </>
        )}
      </table>
    </div>
  );
};

export default Table;
